/**
 * Phase 14 — which satellite PUTs a save sends: a standalone container (or
 * a master) writes all three; a follower writes only the sections it keeps
 * as its own. The creative satellite splits in two (texts | send options)
 * and the partial upsert lets each half travel alone. The send options
 * merge the keys the Options tab does not edit back in (the wipe fix).
 * For Media Devoted | Created By Ciro Autiero
 */
import {
  cleanHourFormula,
  lockRouteTestRanking,
  type DropGenSection,
  type DropGenVerdictSources,
  DROPS_SOURCE,
  lanesToPoolJson,
} from '@trafficpulse/shared';

import type { ContainerDraft } from '../../../../drop-generator.types';
import { scalingToBody } from '../satellites/scaling-draft';
import { gatewayVerdictToBody } from '../satellites/gateway-verdict-draft';
import { hitAgainToBody } from '../satellites/hit-again-draft';
import { draftToTree } from '../satellites/verdict-draft';

export interface SectionPut {
  resource: 'gateway' | 'creative' | 'verdict';
  body: Record<string, unknown>;
}

export function sectionPuts(draft: ContainerDraft): SectionPut[] {
  const following = draft.kind !== 'MASTER' && draft.masterId !== null;
  const own = (s: DropGenSection) => !following || draft.ownSections[s] === true;
  const out: SectionPut[] = [];
  if (draft.kind === 'ROUTE_TEST')
    out.push({
      resource: 'gateway',
      body: {
        orderFormulaJson: lockRouteTestRanking(draft.gateway.ranking),
        ...(own('gatewayStrategy')
          ? {
              strategy: draft.gateway.strategy,
              orderMode: draft.gateway.orderMode,
              weightsMode: draft.gateway.weightsMode,
            }
          : {}),
        ...(own('gatewayPool')
          ? {
              pool: lanesToPoolJson(draft.gateway.pool),
              senderDefaultsJson: draft.gateway.senderDefaults,
            }
          : {}),
      },
    });
  else if (own('gatewayStrategy') || own('gatewayPool'))
    out.push({
      resource: 'gateway',
      body: {
        strategy: draft.gateway.strategy,
        orderMode: draft.gateway.orderMode,
        weightsMode: draft.gateway.weightsMode,
        // Slider rows still waiting for a metric never reach the API (400 there).
        orderFormulaJson: {
          ...draft.gateway.ranking,
          formula: cleanHourFormula(draft.gateway.ranking.formula),
        },
        // Phase 40 — the pool's lanes, ids kept; the sender IDs ride on them (no staticSenders here).
        pool: lanesToPoolJson(draft.gateway.pool),
        senderDefaultsJson: draft.gateway.senderDefaults,
      },
    });
  const creative = {
    ...(own('creative')
      ? {
          textGroupIds: draft.creative.textGroupIds,
          assignment: draft.creative.assignment,
          textMode: draft.creative.textMode,
          amount: draft.creative.amount,
          translate: draft.creative.translate,
          // Slider rows still waiting for a metric never reach the API (400 there).
          textRankingJson: {
            ...draft.creative.ranking,
            formula: cleanHourFormula(draft.creative.ranking.formula),
          },
        }
      : {}),
    ...(own('options')
      ? {
          dropDefaultsJson: { ...draft.sendDefaultsRaw, ...draft.sendDefaults },
        }
      : {}),
  };
  if (Object.keys(creative).length > 0) out.push({ resource: 'creative', body: creative });
  if (own('verdict')) out.push({ resource: 'verdict', body: verdictBody(draft) });
  return out;
}

export function verdictBody(draft: ContainerDraft): Record<string, unknown> {
  return {
    enabled: draft.verdict.enabled,
    minSampleSent: draft.verdict.minSampleSent,
    evaluationDelayMin: draft.verdict.evaluationDelayMin,
    listTimesN: draft.verdict.timesN,
    listTimesMode: draft.verdict.timesMode,
    burnRestDays: draft.verdict.burnRestDays,
    returnTestAmount: draft.verdict.returnTestAmount,
    // An advanced tree (edited via API) is preserved by OMISSION — the
    // partial upsert leaves what the editor cannot express.
    ...(draft.verdict.advanced ? {} : { listVerdictJson: draftToTree(draft.verdict) }),
    ...scalingToBody(draft.scaling),
    ...gatewayVerdictToBody(draft.gatewayVerdict),
    ...hitAgainToBody(draft.hitAgain),
    // Phase 39 — which numbers each card judges.
    sourcesJson: draftSources(draft),
  };
}

/** Phase 39 — every card's source, keyed as the satellite stores them. */
export const draftSources = (draft: ContainerDraft): DropGenVerdictSources => ({
  listVerdict: draft.verdict.source ?? DROPS_SOURCE,
  scaling: draft.scaling.source ?? DROPS_SOURCE,
  gatewayVerdict: draft.gatewayVerdict.source ?? DROPS_SOURCE,
  hitAgain: draft.hitAgain.source ?? DROPS_SOURCE,
});
